// src/lib/searchTypes.js — The universal search type registry. One entry per
// searchable data type: label, route for a result, and the Query page's
// columns. UniversalSearch.js and pages/Query.js both render from this, and
// both fall back to genericType() for any type the server returns that isn't
// listed, so a new type shows up even before it gets an entry here.
//
// Adding a data type: a matcher in src/lib/searchIndex.js, then an entry
// here (see the checklist in CLAUDE.md).

const STAGE_BADGE = {
  'REVIEWING':      'badge-gray',
  'IN DISCUSSIONS': 'badge-blue',
  'NEGOTIATING EL': 'badge-yellow',
  'LIVE':           'badge-green',
  'ON HOLD':        'badge-yellow',
  'ON THE RADAR':   'badge-gray',
  'CLOSED':         'badge-gray',
  'PASSED':         'badge-red',
}

export function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatAum(n) {
  if (n == null) return '—'
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `$${Math.round(n / 1e6)}M`
  return `$${Number(n).toLocaleString()}`
}

const dash = v => (v == null || v === '' || (Array.isArray(v) && !v.length)) ? '—' : v

// Bold the matched part of a name.
export function Highlight({ text, term }) {
  const t = term.toLowerCase()
  const i = (text || '').toLowerCase().indexOf(t)
  if (!t || i < 0) return text
  return (
    <>
      {text.slice(0, i)}
      <mark style={{ background: 'var(--accent-muted)', color: 'inherit', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(i, i + t.length)}
      </mark>
      {text.slice(i + t.length)}
    </>
  )
}

// Why a row came back, e.g. "Match: Growth Equity · Tag: buyout".
export function MatchLine({ match }) {
  if (!match?.length) return null
  return (
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>
      <span style={{ fontWeight: 600 }}>Match:</span> {match.join(' · ')}
    </div>
  )
}

// A person's firms, all equal (migrations/085), one link each.
function FirmLinks({ firms, navigate }) {
  if (!firms?.length) return '—'
  return firms.map((firm, i) => (
    <span key={firm.firm_id}>
      {i > 0 && ', '}
      <span
        onClick={e => { e.stopPropagation(); navigate(`/firms?open=${firm.firm_id}`) }}
        style={{ color: 'var(--blue-text)', cursor: 'pointer' }}
      >
        {firm.name}
      </span>
    </span>
  ))
}

// Column definitions per result type. `cell` renders one row's value.
export function searchTypes(navigate, term) {
  const name = r => (
    <div style={{ minWidth: 180 }}>
      <div style={{ fontWeight: 500 }}><Highlight text={r.name} term={term} /></div>
      <MatchLine match={r.match} />
    </div>
  )
  return [
    {
      key: 'contacts', label: 'Individuals', to: id => `/individuals?open=${id}`,
      columns: [
        { label: 'Name', cell: name },
        { label: 'Title', cell: r => dash(r.title) },
        { label: 'Firms', cell: r => <FirmLinks firms={r.firms} navigate={navigate} /> },
        { label: 'Type', cell: r => r.types?.length ? r.types.map(t => <span key={t} className="badge badge-gray" style={{ marginRight: 4 }}>{t}</span>) : '—' },
        { label: 'Email', cell: r => dash(r.email) },
        { label: 'Owner', cell: r => dash(r.owners?.join(', ')) },
        { label: 'Last Interaction', cell: r => formatDate(r.last_interaction) },
      ],
    },
    {
      key: 'partners', label: 'RGA Partners', to: id => `/rga-partners?open=${id}`,
      columns: [
        { label: 'Name', cell: name },
        { label: 'Firms', cell: r => <FirmLinks firms={r.firms} navigate={navigate} /> },
        { label: 'Title', cell: r => dash(r.title) },
        { label: 'Email', cell: r => dash(r.email) },
        { label: 'Owner', cell: r => dash(r.owners?.join(', ')) },
        { label: 'Last Interaction', cell: r => formatDate(r.last_interaction) },
      ],
    },
    {
      key: 'firms', label: 'Firms', to: id => `/firms?open=${id}`,
      columns: [
        { label: 'Firm Name', cell: name },
        { label: 'Type', cell: r => r.types?.length ? r.types.map(t => <span key={t} className="badge badge-gray" style={{ marginRight: 4 }}>{t}</span>) : '—' },
        { label: 'Investor Subtype', cell: r => dash(r.subtype) },
        { label: 'HQ', cell: r => dash(r.hq) },
        { label: 'AUM / Fund Size', cell: r => formatAum(r.aum) },
        { label: 'Owner', cell: r => dash(r.owners?.join(', ')) },
      ],
    },
    {
      key: 'mandates', label: 'Mandates', to: id => `/mandates/${id}`,
      columns: [
        { label: 'Mandate', cell: name },
        { label: 'GP Firm', cell: r => <FirmLinks firms={r.gp_firm ? [r.gp_firm] : []} navigate={navigate} /> },
        { label: 'Status', cell: r => dash(r.status) },
        { label: 'Stage', cell: r => r.stage ? <span className={`badge ${STAGE_BADGE[r.stage] || 'badge-gray'}`}>{r.stage}</span> : '—' },
        { label: 'Structure / Sector', cell: r => dash(r.sector) },
        { label: 'Target Close', cell: r => dash(r.target_close) },
      ],
    },
    {
      key: 'events', label: 'Events', to: id => `/events/${id}`,
      columns: [
        { label: 'Event', cell: name },
        { label: 'Type', cell: r => r.type ? <span className="badge badge-gray">{r.type}</span> : '—' },
        { label: 'Organizer', cell: r => dash(r.organizer) },
        { label: 'Dates', cell: r => dateRange(r.start_date, r.end_date) },
        { label: 'Location', cell: r => dash(r.location) },
        { label: 'Attendees', cell: r => r.attendees ?? '—' },
      ],
    },
    {
      key: 'ventures', label: 'Rainmaker Ventures', to: id => `/ventures/${id}`,
      columns: [
        { label: 'Venture', cell: name },
        { label: 'Ref', cell: r => <span className="badge badge-blue">{r.ref}</span> },
        { label: 'Owner', cell: r => dash(r.owner) },
      ],
    },
  ]
}

// For a type the server returns that the registry doesn't know yet.
export function genericType(key, navigate, term) {
  return {
    key,
    label: key.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    to: null,
    columns: [
      { label: 'Name', cell: r => (
        <div style={{ minWidth: 180 }}>
          <div style={{ fontWeight: 500 }}><Highlight text={r.name} term={term} /></div>
          <MatchLine match={r.match} />
        </div>
      ) },
      { label: 'Detail', cell: r => dash(r.detail) },
    ],
  }
}

// The registry's types in order, then any extra types present in `data`.
export function resolveTypes(data, navigate, term) {
  const known = searchTypes(navigate, term)
  const keys = new Set(known.map(t => t.key))
  const extra = Object.keys(data ?? {})
    .filter(k => k !== 'concepts' && !keys.has(k))
    .map(k => genericType(k, navigate, term))
  return [...known, ...extra]
}

export function dateRange(start, end) {
  if (!start) return '—'
  const f = iso => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return end && end !== start ? `${f(start)} to ${f(end)}` : f(start)
}

// A multi-line location (letter format) on one line, for lists.
export const oneLine = s => (s || '').split('\n').map(l => l.trim()).filter(Boolean).join(', ')
